import {
  BeakerIcon,
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  UserIcon,
} from '@heroicons/react/outline';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  getRecordHoverColor,
  getRecordBorderColor,
} from '../../screens/EpisodesOfCare/helpers';

const formatDate = (date, options) =>
  new Intl.DateTimeFormat('en-US', options).format(date);

const EpisodeOfCareRecord = ({
  record,
  onClick,
  style,
  showDateStart,
  showBadge,
  showName,
  showTime,
  showClinician,
  showLocation,
  isFullWidth,
}) => {
  const { t } = useTranslation();
  // TODO: Should probably its own prop, for now we use it only in milestones timeline view
  const isCompact = showBadge;
  const { domain, context } = record || {};

  const icon = useMemo(() => {
    switch (domain) {
      case 'appointment':
        return <CalendarIcon className="h-4 w-4 flex-shrink-0" />;
      case 'drug':
        return <BeakerIcon className="h-4 w-4 flex-shrink-0" />;
      default:
        return null;
    }
  }, [domain]);

  const title = useMemo(() => {
    switch (domain) {
      case 'appointment':
        return t('appointments', { count: 1 });
      case 'drug':
        return t('medications', { count: 1 });
      default:
        return null;
    }
  }, [domain, t]);

  return (
    <div className={`group stack-0 ${isFullWidth ? 'w-full' : ''}`}>
      {showBadge && (
        <div
          data-testid="episode-of-care-record-badge"
          className={`z-10 border-r-2 border-t-2 border-l-2 rounded-tl-lg rounded-tr-lg cluster-2 items-center justify-center p-2 transition-colors ${getRecordBorderColor(
            record.status,
          )} ${getRecordHoverColor(record.status)}`}
        >
          {icon}
          <span className="text-xs font-medium truncate">{title}</span>
        </div>
      )}
      <button
        data-testid="episode-of-care-record"
        type="button"
        onClick={() => onClick?.(record.eventId)}
        className={`bg-surface text-on-surface ${
          showBadge ? 'rounded-bl-lg rounded-br-lg' : 'rounded-lg'
        } border-2 py-4 px-2 shadowish-lg cursor-pointer transition-colors z-10 ${getRecordBorderColor(
          record.status,
        )} ${getRecordHoverColor(record.status)}`}
        style={style}
      >
        <div
          className={`flex flex-col justify-center h-full ${
            isCompact ? '' : 'space-y-1'
          }`}
        >
          {showName && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <span
                  data-testid="episode-of-care-record-patient-name"
                  className="font-semibold text-sm truncate"
                >
                  {context.patientName || 'Unknown Patient'}
                </span>
              </div>
            </div>
          )}

          {/* NOTE: Related to medication name */}
          {context.name && (
            <div
              className={`flex items-center ${
                isCompact ? 'justify-center' : ''
              }`}
            >
              {/* {tooltipIcon} */}
              <span
                data-testid="episode-of-care-record-name"
                className={`truncate ${
                  isCompact ? 'text-sm font-medium' : 'text-sm'
                }`}
              >
                {context.name}
              </span>
            </div>
          )}

          {showTime && record.end && (
            <div
              className={`flex items-center ${
                isCompact ? 'justify-center' : ''
              }`}
            >
              {!isCompact && (
                <ClockIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              )}
              <span
                className={`truncate ${
                  isCompact ? 'text-sm font-medium' : 'text-sm'
                }`}
              >
                <span data-testid="episode-of-care-record-time-start">
                  {formatDate(record.start, {
                    timeStyle: 'short',
                  })}
                </span>
                {' • '}
                <span data-testid="episode-of-care-record-duration">
                  {(record.end - record.start) / 60000} min
                </span>
              </span>
            </div>
          )}

          {showDateStart && (
            <div
              className={`flex items-center ${
                isCompact ? 'justify-center' : ''
              }`}
            >
              {!isCompact && (
                <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              )}
              <span
                data-testid="episode-of-care-record-date-start"
                className={`truncate ${
                  isCompact ? 'text-xs font-medium' : 'text-sm'
                }`}
              >
                {formatDate(record.start, {
                  dateStyle: 'medium',
                })}
              </span>
            </div>
          )}

          {showClinician && context.clinicianName && (
            <div
              className={`flex items-center ${
                isCompact ? 'justify-center' : ''
              }`}
            >
              {!isCompact && (
                <UserIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              )}
              <span
                data-testid="episode-of-care-record-clinician-name"
                className={`truncate ${isCompact ? 'text-xs' : 'text-sm'}`}
              >
                {context.clinicianName}
              </span>
            </div>
          )}

          {showLocation && context.location && (
            <div className="flex items-center text-sm">
              <LocationMarkerIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              <span
                data-testid="episode-of-care-record-location"
                className="truncate"
              >
                {context.location.split(',')[0]}
              </span>
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

EpisodeOfCareRecord.propTypes = {
  showBadge: PropTypes.bool,
  showName: PropTypes.bool,
  showDateStart: PropTypes.bool,
  showTime: PropTypes.bool,
  showClinician: PropTypes.bool,
  showLocation: PropTypes.bool,
  isFullWidth: PropTypes.bool,
};

EpisodeOfCareRecord.defaultProps = {
  showBadge: false,
  showName: true,
  showDateStart: false,
  showTime: true,
  showClinician: true,
  showLocation: true,
  isFullWidth: false,
};

export default EpisodeOfCareRecord;
